import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import NoResultGradientImage from '../../../../shared/components/images/no-result-gradient';
import SearchGrey from '../../../../shared/components/images/search-grey';
import {
  setAiSearchPopupInputValue,
  setShowAiSearchPopup,
} from '../../lead-slice';
import {
  getAiSearchShortcut,
  userOsIdentifier,
  placeholderSuggestions,
  getUserRecentSearches,
  aiSuggestions,
} from './helper';
import { accessibleOnClick } from '../../../../shared/utils/accessible-on-click';
import { RootState } from '../../../../store/root-reducer';

/**
 *
 * @returns React Component
 */

const AiSearchBar: React.FC = () => {
  // ** REDUX Hooks
  const dispatch = useDispatch();
  const showAiSearchPopup = useSelector(
    (state: RootState) => state?.leads?.showAiSearchPopup || false,
  );
  const aiSearchInputValue = useSelector(
    (state: RootState) => state?.leads?.aiSearchInputValue,
  );
  const userEmail = useSelector((state: RootState) => state?.home?.email);

  // ** Local State
  const [shortcut, setShortcut] = useState<string>('');
  const [placeholderIndex, setPlaceholderIndex] = useState<number>(0);
  const [animationKey, setAnimationKey] = useState<number>(0);

  // ** Memoized values
  const hasRecentSearch = useMemo(
    () => getUserRecentSearches(userEmail)?.length > 0,
    [userEmail],
  );

  // ** Handlers
  const openAiSearchPopup = useCallback(() => {
    if (!showAiSearchPopup) {
      dispatch(setShowAiSearchPopup(true));
    }
  }, [dispatch, showAiSearchPopup]);

  const handleSuggestionClick = useCallback(
    (text: string) => {
      dispatch(setAiSearchPopupInputValue(text));
      dispatch(setShowAiSearchPopup(true));
    },
    [dispatch],
  );

  // ** Effects
  useEffect(() => {
    const platform = getAiSearchShortcut(userOsIdentifier());
    setShortcut(platform);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholderIndex((prevIndex) => {
        const newIndex = (prevIndex + 1) % placeholderSuggestions?.length;
        setAnimationKey((prevKey) => prevKey + 1);
        return newIndex;
      });
    }, 3000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  return (
    <div className="ai-search-bar-container">
      <div className="search-bar-suggestion-container d-flex">
        <div className="search-bar-header">
          <div className="search-bar-header-text">
            <NoResultGradientImage />
            <span>Accelerate Lead Discovery with AI Search</span>
            <span>Beta</span>
          </div>
          <div
            className="search-bar-input-conatiner"
            {...accessibleOnClick(openAiSearchPopup)}
          >
            <span>
              <SearchGrey />
            </span>
            <input
              type="text"
              className="search-bar-input"
              onFocus={openAiSearchPopup}
              value={aiSearchInputValue}
              readOnly
            />
            <div className="search-bar-shortcut-key">
              <NoResultGradientImage />
              <div className="shortcut-key">{shortcut}</div>
            </div>
            {/* Placeholder Animation block */}
            {aiSearchInputValue?.length === 0 && (
              <>
                <div
                  key={`previous-${animationKey}`}
                  className="animation-placeholder previous"
                >
                  {placeholderIndex > 0
                    ? placeholderSuggestions[placeholderIndex - 1]
                    : placeholderSuggestions[
                        placeholderSuggestions?.length - 1
                      ]}
                </div>
                <div
                  key={`current-${animationKey}`}
                  className="animation-placeholder current"
                >
                  {placeholderSuggestions[placeholderIndex]}
                </div>
              </>
            )}
          </div>
        </div>

        {/* <span
          className={`ai-suggestion-header ${hasRecentSearch ? 'hidden' : ''}`}
        >
          AI Suggestions
        </span>
        <div
          className={`search-ai-suggestion ${hasRecentSearch ? 'hidden' : ''}`}
        >
          {aiSuggestions?.map((item, index) => (
            <div
              className="ai-suggestion-first"
              key={`ai-suggestion ${index + 1}`}
              {...accessibleOnClick(() => handleSuggestionClick(item))}
            >
              <span>{item}</span>
            </div>
          ))}
        </div> */}
      </div>
    </div>
  );
};

export default memo(AiSearchBar);
